import { Injectable, inject } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { CredoMenuService } from 'credo';

// Distribution-only menu: the shared credo menu minus "Order products", plus the
// WolfPack Community Points admin entries. Kept here (not in libs/credo) because the WPCP admin routes only
// exist in the distribution app — surfacing them in fac3less/globalhealth would link
// to non-existent routes. Gated to the super admin (user id 1), matching the backend.
@Injectable({ providedIn: 'root' })
export class DistributionMenuService extends CredoMenuService {
  private auth = inject(AuthService);

  /**
   * Adds the `marketing_enabled` flag (Marketing Intelligence) to the per-user
   * flags. The shared layout passes only the flags every app knows; this one is
   * distribution-only, and the shared rule shows a `flag:` item unless the flag is
   * an explicit `false` — so it is resolved here, fail-closed: only an explicit
   * `true` from the bootstrap payload shows the entry.
   */
  override filterNavItemsByRole(
    isAdmin: boolean,
    userId?: number,
    isXshopAdmin?: boolean,
    isSuperAdmin?: boolean,
    flags?: Record<string, boolean | undefined>,
    roles?: string[]
  ): void {
    super.filterNavItemsByRole(isAdmin, userId, isXshopAdmin, isSuperAdmin, {
      ...(flags ?? {}),
      marketing_enabled: this.auth.getUser()?.marketing_enabled === true
    }, roles);
  }

  protected override createNavItems(lang: string, t: (key: string) => string): INavData[] {
    // Nothing is sold on distribution any more: the monthly/yearly administration
    // fee was the only product here and is retired (v4.0). The WPGH Club membership
    // is bought on wolfpackglobalhealth.com, so an "Order products" entry would lead
    // to an empty wizard. Removed here rather than in the shared credo menu — the
    // other brands still sell.
    // "My orders" stays: the historic orders are still there and still viewable.
    const items = super.createNavItems(lang, t)
      .filter((item) => item.url !== '/' + lang + '/order-wizard');

    // My Cards — member side of the card partner programme, right after
    // "Information" like in fac3less. The page itself decides what to show (link to
    // the partner portal, or the way to buy a card on fac3less).
    const mycards: INavData = {
      name: t('mycards.menu'),
      url: '/' + lang + '/mycards',
      iconComponent: { name: 'cil-credit-card' }
    };
    const infoIdx = items.findIndex(item => item.url === '/' + lang + '/media/info');
    if (infoIdx >= 0) {
      items.splice(infoIdx + 1, 0, mycards);
    } else {
      items.push(mycards);
    }

    // Appointments ("Termine") — the brand's published call schedule, visible to
    // every member. Placed next to Bookings rather than appended, so it does not
    // land below the admin block. The admin CRUD is a separate entry further down.
    const appointments: INavData = {
      name: t('appointments.menu'),
      url: '/' + lang + '/appointments',
      iconComponent: { name: 'cil-calendar' }
    };
    const bookingsIdx = items.findIndex(item => item.url === '/' + lang + '/bookings');
    if (bookingsIdx >= 0) {
      items.splice(bookingsIdx + 1, 0, appointments);
    } else {
      items.push(appointments);
    }

    // Card partner programme. The card programme runs on the fac3less tenant, which
    // this app shares (userDomainId 0), so the data is the same one seen in fac3less
    // — hence the entry in both, and only in those two. Placed next to Card Accounts
    // rather than appended, so it does not land below the fold.
    const entitlements: INavData = {
      name: t('card_entitlements.menu'),
      url: '/' + lang + '/admin/card-entitlements',
      iconComponent: { name: 'cil-credit-card' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' }
    };
    const cardAccountsIdx = items.findIndex(item => item.url === '/' + lang + '/admin/card-accounts');
    if (cardAccountsIdx >= 0) {
      items.splice(cardAccountsIdx + 1, 0, entitlements);
    } else {
      items.push(entitlements);
    }

    // Card partner analytics (monthly summary + cardholder ranking), next to the
    // entitlements it complements.
    const cardPartner: INavData = {
      name: t('card_partner_admin.menu'),
      url: '/' + lang + '/admin/card-partner',
      iconComponent: { name: 'cil-chart' },
      badge: { color: 'danger', text: 'ADMIN' },
      attributes: { 'data-visible': 'isAdmin' }
    };
    const entitlementsIdx = items.findIndex(item => item.url === '/' + lang + '/admin/card-entitlements');
    if (entitlementsIdx >= 0) {
      items.splice(entitlementsIdx + 1, 0, cardPartner);
    } else {
      items.push(cardPartner);
    }

    // Card partner credentials. Super admin only, matching the backend.
    items.push({
      name: t('partner_tokens.menu'),
      url: '/' + lang + '/admin/partner-tokens',
      iconComponent: { name: 'cil-lock-locked' },
      badge: { color: 'danger', text: 'SUPER' },
      attributes: { 'data-visible': 'isSuperAdmin' }
    });

    items.push(
      {
        name: t('appointments.admin_menu'),
        url: '/' + lang + '/admin/appointments',
        iconComponent: { name: 'cil-calendar' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'isAdmin' }
      },
      {
        // Sales Toolkit: the folder members see first under Information,
        // administered per brand (the backend binds every write to it).
        name: t('sales_toolkit.admin_menu'),
        url: '/' + lang + '/admin/sales-toolkit',
        iconComponent: { name: 'cil-folder-open' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'isAdmin' }
      },
      {
        // The suppression list is global (one dead mailbox is dead on every
        // brand) but administered from here only, so the entry stays in this
        // app's menu rather than the shared credo one.
        name: t('email_suppressions.menu_title'),
        url: '/' + lang + '/admin/email-suppressions',
        iconComponent: { name: 'cil-envelope-closed' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'isAdmin' }
      },
      {
        // Sits next to the suppression list: both are about bulk mail and both
        // are administered from this app only.
        name: t('newsletter_stats.menu_title'),
        url: '/' + lang + '/admin/newsletter-stats',
        iconComponent: { name: 'cil-chart' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'isAdmin' }
      },
      {
        // Tracking Engine V1 control view (landing pages of every brand). Next
        // to the newsletter statistics: both are cross-brand measurement tools
        // administered from this app only.
        name: t('tracking.menu_title'),
        url: '/' + lang + '/admin/tracking',
        iconComponent: { name: 'cil-graph' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'isAdmin' }
      },
      {
        // Marketing Intelligence (phase 1): brands, campaigns, costs, audit on top
        // of the tracking data. Visible only with a marketing role (or as platform
        // owner) — MarketingAccess decides, not the admin flag.
        name: t('marketing.menu_title'),
        url: '/' + lang + '/admin/marketing',
        iconComponent: { name: 'cil-bullhorn' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'flag:marketing_enabled' }
      },
      {
        name: t('wpcp.title'),
        url: '/' + lang + '/admin/wpcp',
        iconComponent: { name: 'cil-star' },
        badge: { color: 'danger', text: 'SUPER' },
        attributes: { 'data-visible': 'isSuperAdmin' }
      },
      {
        name: t('wpcp.admin.pool_title'),
        url: '/' + lang + '/admin/wpcp-pool',
        iconComponent: { name: 'cil-dollar' },
        badge: { color: 'danger', text: 'SUPER' },
        attributes: { 'data-visible': 'isSuperAdmin' }
      },
      {
        name: t('wpcp.admin.matrix_title'),
        url: '/' + lang + '/admin/wpcp-matrix',
        iconComponent: { name: 'cil-list-numbered' },
        badge: { color: 'danger', text: 'SUPER' },
        attributes: { 'data-visible': 'isSuperAdmin' }
      },
      {
        // Chain Utils — deliberately the last entry: a toolbox, not daily
        // administration. Distribution-only by placement; the endpoints are
        // brand-agnostic (public chain data).
        name: t('chain_utils.menu_title'),
        url: '/' + lang + '/admin/chain-utils',
        iconComponent: { name: 'cil-link-alt' },
        badge: { color: 'danger', text: 'ADMIN' },
        attributes: { 'data-visible': 'isAdmin' }
      }
    );
    return items;
  }
}
